// src/components/dashboard/DecoratePage.jsx
import React, { Component } from 'react';
import axios from 'axios';
import { urlMap, getAuthHeaders } from '../../utils';
import { withRouter } from "../navigation/withNavigation";

import SelectedVideoPanel from './decoratePage/SelectedVideoPanel';
import ChunksPanel from './decoratePage/ChunkPanel';
import DemoEditPreview from './decoratePage/DemoEditPreview';

class DecoratePage extends Component {
  state = {
    loading: true,
    error: null,

    // single selected video only
    selectedVideo: null, // { id, key, name, url }

    meta: {},                 // { [key]: { duration, width, height } }
    splitting: false,
    splitMessage: null,
    chunkUrls: [],            // presigned GET URLs of chunks
    jobId: null,
    jobStatus: null,          // 'queued' | 'pending' | 'running' | 'cancelling' | 'succeeded' | 'failed' | 'canceled'
    existing: {},             // cache by key
  };

  pollTimer = null;
  ws = null;

  // src/components/dashboard/DecoratePage.jsx
async componentDidMount() {
  const videoId = this.props.params?.id;  // <-- use .id
  if (!videoId) {
    // Seed a dummy selection so the editor preview can render
    const selectedVideo = { id: 'demo', key: 'demo', name: 'Demo Preview', url: '' };
    this.setState({ loading: false, error: null, selectedVideo, chunkUrls: [], jobId: null, jobStatus: null });
    return;
  }
  await this.loadVideoById(videoId);
}

  componentWillUnmount() {
    this.teardownPolling();
    this.teardownWS();
  }

  async componentDidUpdate(prevProps) {
    const prevId = prevProps.params?.id;
    const currId = this.props.params?.id;   // <-- use .id
    if (prevId !== currId && currId) {
      await this.loadVideoById(currId);
    }
  }

  loadVideoById = async (videoId) => {
    try {
      this.setState({ loading: true, error: null });
      // Build the URL using :id since that’s your route
      let getUrl = urlMap.getVideoById || `/worker/video/${videoId}`;
      getUrl = getUrl.replace(':id', videoId);  // <-- replace :id
  
      const { data } = await axios.get(getUrl, { headers: getAuthHeaders() });
      const selectedVideo = {
        id: data.id,
        key: data.key,
        name: data.name || data.original_name,
        url: data.url,
      };
      this.setState(
        { selectedVideo, loading: false, error: null, chunkUrls: [], jobId: null, jobStatus: null },
        () => this.checkExistingSplit(selectedVideo, { attach: true, force: true })
      );
    } catch (err) {
      console.error('Load video failed:', err);
      this.setState({ loading: false, error: 'Failed to load video' });
    }
  };

  // ---- WS / Polling helpers (unchanged) ----
  setupWS = (jobId) => {
    try {
      const base = window.location.host;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const path = (urlMap.wsSplitBase || '/engine/ws/split/') + jobId;

      const token = localStorage.getItem('token');
      const qs = token ? `?token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${proto}://${base}${path}${qs}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.setState((s) => ({
          splitMessage: s.splitMessage || `Job ${jobId} queued… (live updates on)`,
        }));
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'chunk' && msg.url) {
            this.setState((s) => {
              const next = [...s.chunkUrls, msg.url];
              const firstHint = s.chunkUrls.length === 0 ? `First chunk ready (1)` : `Chunks: ${next.length}`;
              return { chunkUrls: next, splitMessage: firstHint };
            });
          } else if (msg.type === 'status' && msg.status) {
            this.setState({ jobStatus: msg.status, splitMessage: `Status: ${msg.status}` });
          }
        } catch { /* ignore malformed */ }
      };

      this.ws.onclose = () => { /* polling continues */ };
    } catch { /* WS optional */ }
  };

  teardownWS = () => {
    if (this.ws) {
      try { this.ws.close(); } catch {}
      this.ws = null;
    }
  };

  setupPolling = (jobId) => {
    this.teardownPolling();
    this.pollTimer = setInterval(() => this.pollStatus(jobId), 5000);
  };

  teardownPolling = () => {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  };

  // ---- UI helpers ----
  handleLoadedMetadata = (key, ev) => {
    const v = ev.currentTarget;
    const info = {
      duration: v.duration || 0,
      width: v.videoWidth || 0,
      height: v.videoHeight || 0,
    };
    this.setState((s) => ({ meta: { ...s.meta, [key]: info } }));
  };

  handleVideoError = (label, ev) => {
    const e = ev?.currentTarget?.error;
    console.warn(`Video error (${label})`, e);
  };

  formatDuration = (seconds) => {
    const s = Math.floor(seconds || 0);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return hh > 0
      ? `${hh}:${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
      : `${mm}:${String(ss).padStart(2,'0')}`;
  };

  // ---- Split job flow (unchanged) ----
  handleSplit = async () => {
    const { selectedVideo } = this.state;
    if (!selectedVideo?.url) return;

    this.teardownPolling();
    this.teardownWS();
    this.setState({
      splitting: true,
      splitMessage: 'Starting…',
      chunkUrls: [],
      jobId: null,
      jobStatus: 'queued',
    });

    try {
      const startUrl = urlMap.split25Start || '/engine/video/split25/start';
      const { data } = await axios.post(
        startUrl,
        {
          presigned_url: selectedVideo.url,
          folder_name: selectedVideo.name || undefined,
          content_type: 'video/mp4',
        },
        { headers: getAuthHeaders() }
      );

      const jobId = data?.job_id;
      if (!jobId) throw new Error('No job_id returned');

      this.setState({
        jobId,
        jobStatus: data?.status || 'queued',
        splitMessage: `Job ${jobId} queued…`,
      });

      this.setupWS(jobId);
      this.setupPolling(jobId);
    } catch (err) {
      console.error('Split start failed:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to start split job.';
      this.setState({ splitting: false, splitMessage: msg, jobId: null, jobStatus: null });
    }
  };

  pollStatus = async (jobId) => {
    try {
      const statusUrl = (urlMap.split25Status || '/engine/video/split25/status/') + jobId;

      const { data } = await axios.get(statusUrl, { headers: getAuthHeaders() });
      const status = data?.status;
      this.setState({ jobStatus: status, splitMessage: `Status: ${status}` });

      if (status === 'succeeded') {
        this.teardownPolling();
        await this.fetchResult(jobId);
        this.teardownWS();
      } else if (status === 'failed' || status === 'canceled') {
        this.teardownPolling();
        this.teardownWS();
        this.setState({
          splitting: false,
          splitMessage: status === 'failed' ? (data?.error || 'Split failed.') : 'Canceled.',
        });
      }
    } catch (err) {
      console.error('Status poll failed:', err);
      this.setState({ splitMessage: 'Lost connection while polling… retrying.' });
    }
  };

  fetchResult = async (jobId) => {
    try {
      const resultUrl = (urlMap.split25Result || '/engine/video/split25/result/') + jobId;
      const { data } = await axios.get(resultUrl, { headers: getAuthHeaders() });

      const urls = Array.isArray(data?.chunks) ? data.chunks : [];
      this.setState({
        splitting: false,
        splitMessage: `Ready: ${urls.length} chunks`,
        chunkUrls: urls,
      });
    } catch (err) {
      console.error('Fetch result failed:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to fetch result.';
      this.setState({ splitting: false, splitMessage: msg });
    }
  };

  handleCancel = async () => {
    const { jobId, jobStatus } = this.state;
    if (!jobId || !['queued', 'pending', 'running', 'cancelling'].includes(jobStatus || '')) return;

    try {
      const cancelUrl = urlMap.split25Cancel || '/engine/video/split25/cancel';
      await axios.post(cancelUrl, { job_id: jobId }, { headers: getAuthHeaders() });
      this.setState({ splitMessage: 'Cancellation requested…', jobStatus: 'cancelling' });
    } catch (err) {
      console.error('Cancel failed:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to request cancellation.';
      this.setState({ splitMessage: msg });
    }
  };

  // ---- Existing split lookup (unchanged) ----
  checkExistingSplit = async (video, { attach = true, force = false } = {}) => {
    if (!video?.url) return null;

    const cached = this.state.existing?.[video.key];
    if (!force && cached?.probed) {
      if (attach && this.state.selectedVideo?.key === video.key) {
        if (Array.isArray(cached.chunks) && cached.chunks.length) {
          this.teardownPolling();
          this.teardownWS();
          this.setState({
            chunkUrls: cached.chunks,
            splitting: false,
            jobId: cached.jobId || null,
            jobStatus: cached.status || null,
            splitMessage: `Found existing split (${cached.chunks.length} chunks)`,
          });
        } else if (cached.status && cached.status !== 'succeeded' && cached.jobId) {
          this.teardownPolling();
          this.teardownWS();
          this.setState({
            jobId: cached.jobId,
            jobStatus: cached.status,
            splitMessage: `Resumed job ${cached.jobId} (status: ${cached.status})`,
            splitting: ['queued','pending','running','cancelling'].includes(cached.status),
          });
          this.setupWS(cached.jobId);
          this.setupPolling(cached.jobId);
        }
      }
      return cached;
    }

    try {
      const lookupUrl = urlMap.lookupSplitList; // /video_chunk/split25/lookup
      const { data } = await axios.get(lookupUrl, {
        headers: getAuthHeaders(),
        params: { url: video.url },
      });

      const info = {
        probed: true,
        jobId: data?.job_id || null,
        status: data?.status || null,
        chunks: Array.isArray(data?.chunks) ? data.chunks : [],
        exists: !!data?.exists,
      };

      this.setState((s) => ({ existing: { ...s.existing, [video.key]: info } }));

      if (attach && this.state.selectedVideo?.key === video.key) {
        if (info.status && info.status !== 'succeeded' && info.jobId) {
          this.teardownPolling();
          this.teardownWS();
          this.setState({
            jobId: info.jobId,
            jobStatus: info.status,
            splitMessage: `Resumed job ${info.jobId} (status: ${info.status})`,
            splitting: ['queued','pending','running','cancelling'].includes(info.status),
          });
          this.setupWS(info.jobId);
          this.setupPolling(info.jobId);
        } else if (info.chunks.length) {
          this.teardownPolling();
          this.teardownWS();
          this.setState({
            chunkUrls: info.chunks,
            splitting: false,
            jobId: info.jobId || null,
            jobStatus: info.status || null,
            splitMessage: `Found existing split (${info.chunks.length} chunks)`,
          });
        } else {
          this.setState({ splitMessage: info.exists ? 'No playable chunks yet.' : 'No split found.' });
        }
      }

      return info;
    } catch (err) {
      console.error('lookup failed:', err);
      this.setState((s) => ({
        existing: { ...s.existing, [video.key]: { probed: true, exists: false, chunks: [] } },
      }));
      return null;
    }
  };

  handleDeleteSplit = async () => {
    const { selectedVideo } = this.state;
    if (!selectedVideo?.url) return;

    const url = selectedVideo.url;
    try {
      await axios.delete(urlMap.deleteSplit, {
        headers: getAuthHeaders(),
        params: { url }
      });

      // prune local chunk URLs in that folder
      const m = url.match(/https?:\/\/[^/]+\/([^?]+)/);
      if (!m || !m[1]) throw new Error("Could not parse key from url");
      const clickedKey = decodeURIComponent(m[1]);
      const folderPrefix = clickedKey.slice(0, clickedKey.lastIndexOf("/") + 1);

      this.setState(s => ({
        chunkUrls: s.chunkUrls.filter(u => {
          const m2 = u.match(/https?:\/\/[^/]+\/([^?]+)/);
          if (!m2 || !m2[1]) return true;
          const key = decodeURIComponent(m2[1]);
          return !key.startsWith(folderPrefix);
        })
      }));
    } catch (err) {
      console.error("Failed to delete split folder:", err);
      alert("Failed to delete split folder. Check console for details.");
    }
  };

  render() {
    const { loading, error, selectedVideo, meta, splitting, splitMessage, chunkUrls, jobId, jobStatus } = this.state;

    if (loading) return <p>Loading video…</p>;
    if (!selectedVideo) {
      // As a fallback, still show demo preview
      return (
        <div className="split-widget">
          {error && <p className="error">{error}</p>}
          <DemoEditPreview videoUrl={''} />
        </div>
      );
    }

    const m = meta[selectedVideo.key];
    const canCancel = jobId && ['queued', 'pending', 'running', 'cancelling'].includes(jobStatus || '');

    return (
      <div className="split-widget">
        {chunkUrls.length > 0 ? (
          <div className="split-layout">
            {selectedVideo.url && (
              <SelectedVideoPanel
                video={selectedVideo}
                meta={m}
                formatDuration={this.formatDuration}
                onMeta={(e) => this.handleLoadedMetadata(selectedVideo.key, e)}
                onSplit={this.handleSplit}
                onCancel={this.handleCancel}
                canCancel={canCancel}
                splitting={splitting}
                splitMessage={splitMessage}
                jobId={jobId}
                jobStatus={jobStatus}
                handleVideoError={this.handleVideoError}
                handleDeleteSplit={this.handleDeleteSplit}
              />
            )}
            <ChunksPanel chunks={chunkUrls} handleVideoError={this.handleVideoError} />
            {/* Demo editor preview under the split UI */}
            <DemoEditPreview videoUrl={selectedVideo.url} />
          </div>
        ) : (
          selectedVideo.url ? (
            <SelectedVideoPanel
              video={selectedVideo}
              meta={m}
              formatDuration={this.formatDuration}
              onMeta={(e) => this.handleLoadedMetadata(selectedVideo.key, e)}
              onSplit={this.handleSplit}
              onCancel={this.handleCancel}
              canCancel={canCancel}
              splitting={splitting}
              splitMessage={splitMessage}
              jobId={jobId}
              jobStatus={jobStatus}
              handleVideoError={this.handleVideoError}
            />
          ) : null
        )}
        {/* Also show the demo preview when there are no chunks yet */}
        {chunkUrls.length === 0 && (
          <DemoEditPreview videoUrl={selectedVideo.url} />
        )}
      </div>
    );
  }
}

export default withRouter(DecoratePage);
